import axios from "axios";
import { defineStore } from "pinia";
import { reactive, ref } from "vue";
import { useGlobalMessagesStore } from "./globalMessagesStore";
import { Audit, AuditDocument, DocumentTemplate, DocumentTemplateUpdateRequest, PagedList } from "@/audit.types";
import { BTableOptions, serializeSortBy } from "@/components/BTable/BTable.types";

import { handleError } from "./storeHelper";
import { BFilterableField, BFilterOptions, dateTimeOperations, textOperations } from "@/components/BFilterEditor/BFilterEditor.types";
import { serializeFilters } from "@/components/BTable/HelperFunctions";
const globalMessages = useGlobalMessagesStore();

export const useAuditDocumentTemplateStore = defineStore("auditDocumentTemplateStore", () => {
    const loading = ref<boolean>(false);
    const items = ref<DocumentTemplate[]>();
    const apiUrl = 'api/v1/audit/document-template';

    const filterOptions = ref<BFilterOptions>({
        filters: [],
    });
    const tableOptions = ref<BTableOptions>({
        totalItemCount: 0,
        itemsPerPage: 25,
        page: 1,
        sortBy: [],
        filterBy: [],
        pagingMode: "withTotal"
    } as BTableOptions);

    const filterableFields = reactive<BFilterableField[]>([
        {
            property: "name",
            label: "Document Template Name",
            operations: textOperations,
            editorComponent: "text-editor",
        },
        {
            property: "description",
            label: "Description",
            operations: textOperations,
            editorComponent: "text-editor",
        },
        {
            property: "updated-date",
            label: "Updated Date",
            operations: dateTimeOperations,
            editorComponent: "date-editor",
        },
    ]);

    const list = async () => {

        try {
            loading.value = true;

            let filterParam = serializeFilters(filterOptions.value.filters, filterableFields);
            let sortParam = serializeSortBy(tableOptions.value.sortBy);

            const response = await axios.get<PagedList<DocumentTemplate>>(apiUrl, {
                params: {
                    limit: tableOptions.value.itemsPerPage,
                    offset: tableOptions.value.itemsPerPage * (tableOptions.value.page - 1),
                    sort: sortParam,
                    filter: filterParam,
                }
            });
            items.value = response.data.data;
            tableOptions.value.totalItemCount = response.data.total;
        }
        catch (error) {
            handleError(error);
            throw error;
        }
        finally {
            loading.value = false;
        }
    }

    const create = async (documentTemplate: DocumentTemplate): Promise<DocumentTemplate> => {
        try {
            loading.value = true;

            let formData = new FormData();
            formData.append('name', documentTemplate.name);
            formData.append('description', documentTemplate.description);
            if (documentTemplate.file) {
                formData.append('file', documentTemplate.file);
            }

            const response = await axios.put<DocumentTemplate>(apiUrl, formData);
            globalMessages.push({ message: "Document template created successfully.", type: "success" })
            return response.data;
        }
        catch (error) {
            handleError(error);
            throw error;
        }
        finally {
            loading.value = false;
        }
    }

    const replace = async (item: DocumentTemplateUpdateRequest): Promise<DocumentTemplateUpdateRequest> => {
        try {
            loading.value = true;

            let formData = new FormData();
            if (item.file) {
                formData.append('file', item.file);
            }
            const response = await axios.post<DocumentTemplateUpdateRequest>(`${apiUrl}/${item.id}`, formData);
            globalMessages.push({ message: "Document template replaces successfully.", type: "success" })
            return response.data;
        }
        catch (error) {
            handleError(error);
            throw error;
        }
        finally {
            loading.value = false;
        }
    }


    const getById = async (id: number): Promise<DocumentTemplate> => {

        try {
            loading.value = true;
            const response = await axios.get<DocumentTemplate>(`${apiUrl}/${id}/details`);
            return response.data;
        }
        catch (error) {
            handleError(error);
            throw error;
        }
        finally {
            loading.value = false;
        }
    }

    const deleteItem = async (id: number): Promise<void> => {
        try {
            loading.value = true;
            const response = await axios.delete<AuditDocument>(`${apiUrl}/${id}`);
            globalMessages.push({ message: "Document template deleted successfully.", type: "success" })
        }
        catch (error) {
            handleError(error);
            throw error;
        }
        finally {
            loading.value = false;
        }
    }

    return {
        tableOptions,
        filterOptions,
        filterableFields,
        loading,
        items,
        create,
        getById,
        replace,
        deleteItem,
        list
    };
})